import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface UseNoteModalOptions {
  basePath?: string;
}

interface UseNoteModalReturn {
  selectedNoteId: string | null;
  isModalOpen: boolean;
  openModal: (publicId: string) => void;
  closeModal: () => void;
}

export function useNoteModal(options: UseNoteModalOptions = {}): UseNoteModalReturn {
  const { basePath = '/notes' } = options;
  const location = useLocation();

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  // Parse note ID from URL on mount and URL changes
  useEffect(() => {
    const pathMatch = location.pathname.match(new RegExp(`^${basePath}/([^/]+)$`));
    if (pathMatch) {
      setSelectedNoteId(pathMatch[1]);
    } else {
      setSelectedNoteId(null);
    }
  }, [location.pathname, basePath]);

  // Listen for popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const pathMatch = window.location.pathname.match(new RegExp(`^${basePath}/([^/]+)$`));
      if (pathMatch) {
        setSelectedNoteId(pathMatch[1]);
      } else {
        setSelectedNoteId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [basePath]);

  const openModal = useCallback((publicId: string) => {
    setSelectedNoteId(publicId);

    // Preserve existing query params
    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();
    const newUrl = `${basePath}/${publicId}${queryString ? `?${queryString}` : ''}`;

    window.history.pushState({ noteModal: true, publicId }, '', newUrl);
  }, [basePath]);

  const closeModal = useCallback(() => {
    setSelectedNoteId(null);

    // Preserve existing query params when returning to list
    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();
    const newUrl = `${basePath}${queryString ? `?${queryString}` : ''}`;

    // Use replaceState to avoid creating a back-button loop
    window.history.replaceState(null, '', newUrl);
  }, [basePath]);

  return {
    selectedNoteId,
    isModalOpen: selectedNoteId !== null,
    openModal,
    closeModal,
  };
}
